/* Admin · Legacy & Goodwill → RFQ Seeder (Phase 1: generator, queue, approval). */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Radio, Trash2, CheckSquare, Square, Loader2, RefreshCw, Hash, MapPin, CalendarClock, Users,
  Search, ChevronDown, ChevronUp, X,
} from "lucide-react";

const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace';
const TABS = [["pending", "Queue"], ["broadcast", "Broadcast"], ["deleted", "Retracted"]];

export function AdminLegacyGoodwill({ api }) {
  return (
    <div className="space-y-6" data-testid="legacy-goodwill">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C9A961]">Legacy &amp; Goodwill</div>
          <h2 className="font-serif text-2xl md:text-3xl text-[#F5F5F0] mt-1">RFQ Seeder</h2>
          <p className="font-body text-sm text-[#8A8A82] mt-1 max-w-2xl">
            Auto-drafts realistic buyer RFQs from the live catalogue and empaneled buyer base. Nothing goes live
            until you approve — approved RFQs file through the real Buyer-RFQ pipeline and the anonymised broadcast desk.
          </p>
        </div>
      </div>
      <SchedulerPanel api={api} />
      <RfqSeederQueue api={api} />
    </div>
  );
}

function SchedulerPanel({ api }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { setS((await api.get("/admin/rfq-seeder/scheduler")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load scheduler"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const update = async (patch, label) => {
    setBusy(label);
    try {
      const { data } = await api.put("/admin/rfq-seeder/scheduler", patch);
      setS(data);
      toast.success(patch.enabled === undefined ? `Batch size per run → ${patch.batch_size}` : (patch.enabled ? "Auto-Scheduler ON" : "Auto-Scheduler OFF"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  const runNow = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/admin/rfq-seeder/scheduler/run-now");
      setS(data.scheduler);
      const r = data.run;
      toast[r.status === "ok" ? "success" : "message"](r.status === "ok" ? `Run drafted ${r.generated} RFQs into the queue` : `Run ${r.status}: ${r.reason || ""}`);
      window.dispatchEvent(new Event("seeder:refresh"));
    } catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setBusy(null); }
  };

  if (!s) return null;
  const on = s.enabled;
  return (
    <section data-testid="seeder-scheduler" className="rounded-sm border bg-black/40 p-5 space-y-5"
             style={{ borderColor: on ? "rgba(47,185,153,0.45)" : "rgba(255,255,255,0.1)" }}>
      <div className="flex flex-wrap items-center gap-5">
        <button onClick={() => update({ enabled: !on }, "toggle")} disabled={busy === "toggle"}
                data-testid="seeder-scheduler-toggle" aria-pressed={on}
                className="flex items-center gap-3 group">
          <span className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${on ? "bg-[#2FB999]" : "bg-[#3A3A38]"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-[#F5F5F0] shadow transition-transform ${on ? "translate-x-8" : "translate-x-1"}`} />
          </span>
          <span>
            <span className="block font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">Auto-Scheduler</span>
            <span data-testid="seeder-scheduler-state" className={`block font-serif text-lg ${on ? "text-[#2FB999]" : "text-[#8A8A82]"}`}>{on ? "ON" : "OFF"}</span>
          </span>
        </button>

        <div className="flex items-center gap-3 border-l border-white/10 pl-5">
          <label className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82]">Batch size per run</label>
          <select data-testid="seeder-scheduler-batch" value={s.batch_size} disabled={busy === "batch"}
                  onChange={(e) => update({ batch_size: Number(e.target.value) }, "batch")}
                  className="rounded-sm border border-white/10 bg-black/60 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>

        <div className="border-l border-white/10 pl-5">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82]">Next scheduled run</div>
          <div style={{ fontFamily: MONO }} data-testid="seeder-next-run" className={`text-sm mt-0.5 ${on ? "text-[#F5F5F0]" : "text-[#6f6f68] line-through"}`}>{s.next_run_ist}</div>
          <div className="font-grotesk text-[8px] uppercase tracking-[0.2em] text-[#6f6f68] mt-0.5">Runs {s.run_times_ist.join(" · ")} IST{on ? "" : " · paused"}</div>
        </div>

        <button onClick={runNow} disabled={!!busy} data-testid="seeder-run-now"
                className="ml-auto inline-flex items-center gap-2 rounded-sm border border-[#C9A961]/50 px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-40">
          {busy === "run" ? <Loader2 size={12} className="animate-spin" /> : <CalendarClock size={12} />} Run now
        </button>
      </div>

      <div>
        <div className="font-grotesk text-[9px] uppercase tracking-[0.26em] text-[#8A8A82] mb-2">Last {Math.min(10, s.runs.length)} scheduler runs</div>
        {s.runs.length === 0 ? (
          <p className="font-body text-xs text-[#6f6f68]" data-testid="seeder-runs-empty">No runs yet — the first batch lands at {s.next_run_ist}.</p>
        ) : (
          <div className="border border-white/10 rounded-sm overflow-x-auto" data-testid="seeder-runs">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02] font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#8A8A82]">
                  {["Run (IST)", "Trigger", "Batch", "Generated", "Approved", "Pending", "Status"].map((h) => <th key={h} className="px-3 py-2 whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {s.runs.map((r) => (
                  <tr key={r.run_id} data-testid={`seeder-run-${r.run_id}`}>
                    <td style={{ fontFamily: MONO }} className="px-3 py-2 text-[#C9C9C2] whitespace-nowrap">{r.started_at_ist}</td>
                    <td className="px-3 py-2 text-[#8A8A82] uppercase text-[9px] tracking-[0.18em]">{r.trigger || "cron"}</td>
                    <td style={{ fontFamily: MONO }} className="px-3 py-2 text-[#C9C9C2]">{r.batch_size ?? "—"}</td>
                    <td style={{ fontFamily: MONO }} className="px-3 py-2 text-[#F5F5F0]">{r.generated ?? 0}</td>
                    <td style={{ fontFamily: MONO }} className="px-3 py-2 text-[#2FB999]">{r.approved}</td>
                    <td style={{ fontFamily: MONO }} className="px-3 py-2 text-[#C9A961]">{r.pending}</td>
                    <td className="px-3 py-2">
                      <span className="font-grotesk text-[8px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-sm"
                            style={{ color: r.status === "ok" ? "#2FB999" : r.status === "skipped" ? "#8A8A82" : r.status === "failed" ? "#E5484D" : "#C9A961",
                                     background: "rgba(255,255,255,0.04)" }} title={r.reason || ""}>
                        {r.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function RfqSeederQueue({ api }) {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(5);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [pools, setPools] = useState({ buyers: [], sellers: [], saved: { buyer_ids: [], seller_ids: [] } });
  const [buyerSel, setBuyerSel] = useState([]);
  const [sellerSel, setSellerSel] = useState([]);

  const loadPools = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/rfq-seeder/pools");
      setPools(data);
      setBuyerSel(data.saved?.buyer_ids || []);
      setSellerSel(data.saved?.seller_ids || []);
    } catch { /* non-fatal */ }
  }, [api]);
  useEffect(() => { loadPools(); }, [loadPools]);

  const savePools = async () => {
    setBusy("pools");
    try {
      const { data } = await api.put("/admin/rfq-seeder/pools", { buyer_ids: buyerSel, seller_ids: sellerSel });
      setPools((p) => ({ ...p, saved: data.saved }));
      toast.success(buyerSel.length || sellerSel.length
        ? `Default pools saved · ${buyerSel.length || "all"} buyers · ${sellerSel.length || "all"} sellers — scheduler will respect these`
        : "Pools cleared — scheduler back to full randomisation");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save pools"); }
    finally { setBusy(null); }
  };
  const poolsDirty = JSON.stringify([...buyerSel].sort()) !== JSON.stringify([...(pools.saved?.buyer_ids || [])].sort())
    || JSON.stringify([...sellerSel].sort()) !== JSON.stringify([...(pools.saved?.seller_ids || [])].sort());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/rfq-seeder/queue?status=${tab}`);
      setItems(data.items || []); setCounts(data.counts || {});
      setMeta({ buyers: data.eligible_buyers, sellers: data.approved_sellers, categories: data.categories || [] });
      setSelected(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load the seeder queue"); }
    finally { setLoading(false); }
  }, [api, tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("seeder:refresh", h);
    return () => window.removeEventListener("seeder:refresh", h);
  }, [load]);

  const generate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post("/admin/rfq-seeder/generate", { count, buyer_ids: buyerSel, seller_ids: sellerSel });
      toast.success(`${data.generated} RFQ draft${data.generated === 1 ? "" : "s"} added to the queue${data.buyer_pool ? ` · ${data.buyer_pool} buyer pool` : ""}${data.seller_pool ? ` · ${data.seller_pool} seller pool` : ""}`);
      if (tab !== "pending") setTab("pending"); else load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setBusy(null); }
  };

  const approve = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/rfq-seeder/queue/${id}/approve`);
      toast.success(`${data.item.ref} broadcast to ${data.item.broadcast_count} suppliers · ${data.item.srn}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };

  const approveBulk = async (all) => {
    const ids = [...selected];
    if (!all && ids.length === 0) return;
    if (!window.confirm(all ? `Approve & broadcast ALL ${counts.pending || 0} pending RFQs?` : `Approve & broadcast ${ids.length} selected RFQ${ids.length === 1 ? "" : "s"}?`)) return;
    setBusy("bulk");
    try {
      const { data } = await api.post("/admin/rfq-seeder/queue/approve-bulk", all ? { all_pending: true } : { ids });
      toast.success(`${data.approved} RFQ${data.approved === 1 ? "" : "s"} broadcast${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk approve failed"); }
    finally { setBusy(null); }
  };

  const remove = async (it) => {
    const msg = it.status === "broadcast"
      ? `Retract ${it.ref}? This deletes the live RFQ, its supplier broadcasts and any quotes.`
      : "Delete this draft from the queue?";
    if (!window.confirm(msg)) return;
    setBusy(it.id);
    try {
      await api.delete(`/admin/rfq-seeder/queue/${it.id}`);
      toast.success(it.status === "broadcast" ? "Seeded RFQ retracted" : "Draft deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(null); }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)));

  return (
    <div className="space-y-5">
      {/* Optional pools */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="seeder-pools">
        <PoolPicker kind="buyers" title="Requester Buyer Pool" hint="Blank = randomise across all eligible empaneled buyers · RealtyF5 Matrix Global & admins are always excluded"
                    rows={pools.buyers} value={buyerSel} onChange={setBuyerSel} />
        <PoolPicker kind="sellers" title="Broadcast Seller Pool" hint="Blank = broadcast to all approved sellers"
                    rows={pools.sellers} value={sellerSel} onChange={setSellerSel} />
        <div className="lg:col-span-2 flex flex-wrap items-center justify-between gap-3 -mt-1">
          <span className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#6f6f68]" data-testid="seeder-pools-status">
            {pools.saved?.buyer_ids?.length || pools.saved?.seller_ids?.length
              ? `Saved defaults · ${pools.saved.buyer_ids.length || "all"} buyers · ${pools.saved.seller_ids.length || "all"} sellers · scheduler respects these`
              : "No saved pools · scheduler randomises across all"}
            {poolsDirty && <span className="text-[#C9A961]"> · unsaved changes apply to this Generate only</span>}
          </span>
          <button onClick={savePools} disabled={!poolsDirty || busy === "pools"} data-testid="seeder-pools-save"
                  className="rounded-sm border border-[#C9A961]/50 px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-40">
            {busy === "pools" ? <Loader2 size={11} className="animate-spin inline" /> : null} Save as default for scheduler
          </button>
        </div>
      </div>

      {/* Generator bar */}
      <div className="rounded-sm border border-[#C9A961]/30 bg-black/40 p-4 flex flex-wrap items-center gap-4" data-testid="seeder-generator">
        <div className="flex items-center gap-3">
          <label className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#8A8A82]">Batch size</label>
          <select data-testid="seeder-batch-size" value={count} onChange={(e) => setCount(Number(e.target.value))}
                  className="rounded-sm border border-white/10 bg-black/60 px-3 py-2 text-sm text-[#F5F5F0] outline-none focus:border-[#C9A961]">
            {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button onClick={generate} disabled={busy === "generate"} data-testid="seeder-generate"
                className="inline-flex items-center gap-2 rounded-sm bg-[#C9A961] px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.26em] text-black hover:bg-[#dcbd77] disabled:opacity-50 shadow-[0_0_18px_rgba(201,169,97,0.25)]">
          {busy === "generate" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate
        </button>
        <div className="ml-auto flex flex-wrap items-center gap-4 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82]">
          <span className="inline-flex items-center gap-1.5" data-testid="seeder-meta-buyers"><Users size={11} /> {meta.buyers ?? "—"} eligible buyers</span>
          <span className="inline-flex items-center gap-1.5" data-testid="seeder-meta-sellers"><Radio size={11} /> {meta.sellers ?? "—"} approved suppliers</span>
          <span>{(meta.categories || []).join(" · ") || "—"}</span>
        </div>
      </div>

      {/* Tabs + bulk bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10">
        <div className="flex items-center gap-1">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`seeder-tab-${k}`}
                    className={`px-4 py-2.5 -mb-px border-b-2 font-grotesk text-[10px] uppercase tracking-[0.26em] transition-colors ${
                      tab === k ? "border-[#C9A961] text-[#F5F5F0]" : "border-transparent text-[#8A8A82] hover:text-[#C9C9C2]"}`}>
              {label} <span style={{ fontFamily: MONO }} className={tab === k ? "text-[#C9A961]" : ""}>{counts[k] || 0}</span>
            </button>
          ))}
          <button onClick={load} className="ml-2 p-2 text-[#8A8A82] hover:text-[#F5F5F0]" title="Refresh" data-testid="seeder-refresh"><RefreshCw size={12} /></button>
        </div>
        {tab === "pending" && items.length > 0 && (
          <div className="flex items-center gap-2 pb-2">
            <button onClick={toggleAll} data-testid="seeder-select-all"
                    className="inline-flex items-center gap-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9C9C2] hover:text-white px-2 py-1.5">
              {allSelected ? <CheckSquare size={13} /> : <Square size={13} />} {allSelected ? "Clear" : "Select all"}
            </button>
            <button onClick={() => approveBulk(false)} disabled={selected.size === 0 || !!busy} data-testid="seeder-approve-selected"
                    className="rounded-sm border border-[#C9A961]/50 px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-40">
              Approve selected ({selected.size})
            </button>
            <button onClick={() => approveBulk(true)} disabled={!!busy} data-testid="seeder-approve-all"
                    className="rounded-sm bg-[#2FB999] px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-black hover:bg-[#45cfae] disabled:opacity-40">
              {busy === "bulk" ? <Loader2 size={11} className="animate-spin inline" /> : null} Approve &amp; Broadcast all
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="font-body text-[#8A8A82] py-10 text-center">Loading…</p>
      ) : items.length === 0 ? (
        <p className="font-body text-[#8A8A82] py-14 text-center border border-dashed border-white/10 rounded-sm" data-testid="seeder-empty">
          {tab === "pending" ? "Queue is empty — set a batch size and click Generate." : "Nothing here yet."}
        </p>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((it) => (
            <SeedCard key={it.id} it={it} tab={tab} busy={busy} selected={selected.has(it.id)}
                      onToggle={() => toggle(it.id)} onApprove={() => approve(it.id)} onRemove={() => remove(it)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PoolPicker({ kind, title, hint, rows, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const set = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = s ? rows.filter((r) => [r.email, r.name, r.business, r.code, r.city, r.state].some((v) => (v || "").toLowerCase().includes(s))) : rows;
    return list.slice(0, 60);
  }, [rows, q]);
  const toggle = (id) => onChange(set.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  const chosen = rows.filter((r) => set.has(r.id));
  return (
    <div className="rounded-sm border border-white/10 bg-black/40 p-4" data-testid={`pool-${kind}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.26em] text-[#C9A961]">{title} <span className="text-[#6f6f68]">· optional</span></div>
          <div className="font-body text-[11px] text-[#8A8A82] mt-0.5">{hint}</div>
        </div>
        <span style={{ fontFamily: MONO }} className="text-xs text-[#F5F5F0] whitespace-nowrap" data-testid={`pool-${kind}-count`}>
          {value.length ? `${value.length} selected` : `all ${rows.length}`}
        </span>
      </div>
      <div className="mt-3 flex items-center gap-2 rounded-sm border border-white/10 bg-black/60 px-3 py-2 focus-within:border-[#C9A961]">
        <Search size={12} className="text-[#8A8A82]" />
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
               placeholder={`Search ${kind} by name, company, email, code or city…`} data-testid={`pool-${kind}-search`}
               className="flex-1 bg-transparent outline-none text-sm text-[#F5F5F0] placeholder:text-[#6f6f68]" />
        {value.length > 0 && (
          <button onClick={() => onChange([])} data-testid={`pool-${kind}-clear`} className="font-grotesk text-[8px] uppercase tracking-[0.2em] text-[#8A8A82] hover:text-[#E5484D]">Clear</button>
        )}
        <button onClick={() => setOpen((o) => !o)} data-testid={`pool-${kind}-toggle`} className="text-[#8A8A82] hover:text-white">
          {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
      </div>
      {chosen.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`pool-${kind}-chips`}>
          {chosen.map((r) => (
            <span key={r.id} className="inline-flex items-center gap-1 rounded-full border border-[#C9A961]/40 bg-[#C9A961]/10 px-2 py-0.5 text-[10px] text-[#F5F5F0]">
              {r.business || r.name || r.email}
              <button onClick={() => toggle(r.id)} className="text-[#8A8A82] hover:text-[#E5484D]" aria-label="remove"><X size={10} /></button>
            </span>
          ))}
        </div>
      )}
      {open && (
        <ul className="mt-2 max-h-56 overflow-y-auto divide-y divide-white/5 border border-white/10 rounded-sm" data-testid={`pool-${kind}-list`}>
          {filtered.length === 0 ? <li className="px-3 py-3 text-xs text-[#6f6f68]">No matches.</li> : filtered.map((r) => (
            <li key={r.id}>
              <button onClick={() => toggle(r.id)} data-testid={`pool-${kind}-opt-${r.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-white/[0.04] ${set.has(r.id) ? "bg-[#C9A961]/10" : ""}`}>
                {set.has(r.id) ? <CheckSquare size={13} className="text-[#C9A961] shrink-0" /> : <Square size={13} className="text-[#6f6f68] shrink-0" />}
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] text-[#F5F5F0] truncate">{r.business || r.name || r.email}
                    {r.excluded_by_default && <span className="ml-2 font-grotesk text-[8px] uppercase tracking-[0.18em] text-[#E0A458]">excluded by default</span>}
                  </span>
                  <span className="block text-[10px] text-[#8A8A82] truncate">{r.email}{r.city || r.state ? ` · ${[r.city, r.state].filter(Boolean).join(", ")}` : ""}</span>
                </span>
                {r.code && <span style={{ fontFamily: MONO }} className="text-[10px] text-[#C9A961] shrink-0">{r.code}</span>}
              </button>
            </li>
          ))}
          {rows.length > 60 && filtered.length === 60 && <li className="px-3 py-2 text-[10px] text-[#6f6f68]">Showing first 60 — refine your search.</li>}
        </ul>
      )}
    </div>
  );
}

function SeedCard({ it, tab, busy, selected, onToggle, onApprove, onRemove }) {
  const r = it.rfq;
  const spec = useMemo(() => [r.material, r.grade, r.diameter ? `⌀ ${r.diameter} mm` : ""].filter(Boolean).join(" · "), [r]);
  const mine = busy === it.id;
  return (
    <article data-testid={`seed-card-${it.id}`}
             className={`rounded-sm border bg-black/40 p-4 flex flex-col gap-3 transition-colors ${
               selected ? "border-[#C9A961]" : "border-white/10 hover:border-white/25"}`}>
      <header className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          {tab === "pending" && (
            <button onClick={onToggle} data-testid={`seed-select-${it.id}`} className="mt-0.5 text-[#C9A961]">
              {selected ? <CheckSquare size={15} /> : <Square size={15} className="text-[#8A8A82]" />}
            </button>
          )}
          <div className="min-w-0">
            <div className="font-serif text-[15px] text-[#F5F5F0] leading-snug truncate">{spec}</div>
            <div className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mt-0.5">{r.catalogue_product}</div>
          </div>
        </div>
        <span style={{ fontFamily: MONO }} className="text-[#C9A961] text-sm whitespace-nowrap" data-testid={`seed-qty-${it.id}`}>
          {r.quantity} {r.unit}
        </span>
      </header>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <Row k="Requester" v={<>{r.company || r.name}<div className="text-[#8A8A82] truncate">{r.email}</div></>} wide />
        <Row k="Delivery" v={<span className="inline-flex items-center gap-1"><MapPin size={10} /> {[r.delivery_city, r.delivery_state].filter(Boolean).join(", ")}</span>} />
        <Row k="Required by" v={<span className="inline-flex items-center gap-1"><CalendarClock size={10} /> {r.target_delivery}</span>} />
        {it.ref && <Row k="Reference" v={<span style={{ fontFamily: MONO }}>{it.ref}</span>} />}
        {it.seller_pool_ids?.length > 0 && <Row k="Seller pool" v={<span data-testid={`seed-pool-${it.id}`}>{it.seller_pool_ids.length} selected supplier{it.seller_pool_ids.length === 1 ? "" : "s"}</span>} />}
        {it.srn && <Row k="VNC-SEED" v={<span style={{ fontFamily: MONO }} className="text-[#C9A961] inline-flex items-center gap-1" data-testid={`seed-srn-${it.id}`}><Hash size={10} />{it.srn}</span>} />}
      </dl>
      <p className="font-body text-[11px] text-[#8A8A82] italic leading-relaxed line-clamp-2">{r.notes}</p>

      <footer className="mt-auto pt-2 border-t border-white/10 flex items-center justify-between gap-2">
        <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#6f6f68]">
          {it.status === "broadcast" ? `Broadcast to ${it.broadcast_count} · ${(it.approved_at || "").slice(0, 10)}`
            : it.status === "deleted" ? `Retracted ${(it.deleted_at || "").slice(0, 10)}`
            : `Drafted ${(it.generated_at || "").slice(0, 10)}`}
        </span>
        <div className="flex items-center gap-2">
          {it.status !== "deleted" && (
            <button onClick={onRemove} disabled={!!busy} title={it.status === "broadcast" ? "Retract live RFQ" : "Delete draft"}
                    data-testid={`seed-delete-${it.id}`}
                    className="p-1.5 text-[#8A8A82] hover:text-[#E5484D] disabled:opacity-40"><Trash2 size={13} /></button>
          )}
          {it.status === "pending" && (
            <button onClick={onApprove} disabled={!!busy} data-testid={`seed-approve-${it.id}`}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-[#C9A961] px-3 py-1.5 font-grotesk text-[9px] uppercase tracking-[0.22em] text-black hover:bg-[#dcbd77] disabled:opacity-40">
              {mine ? <Loader2 size={11} className="animate-spin" /> : <Radio size={11} />} Approve &amp; Broadcast
            </button>
          )}
        </div>
      </footer>
    </article>
  );
}

function Row({ k, v, wide }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <dt className="font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#6f6f68]">{k}</dt>
      <dd className="text-[#C9C9C2] mt-0.5 truncate">{v}</dd>
    </div>
  );
}
